/**
 * @file Photo related queries.
 */
import gql from 'graphql-tag';

import { PhotoUrlTypeFields } from '../fragments/album';
import {
  PhotoFaceHighlightDetails,
  PhotoForegroundDetails,
} from '../fragments/photo';
import { CommentDetails } from '../fragments/comments';
import {
  AccountFileReactionDetails,
  GroupedReactionsDetails,
  ReactionAccountDetails,
} from '../fragments/reactions';

export const PhotoDownloadURL = gql`
  query getPhotoDownloadUrl($photos: [PhotoDownloadUrlsItem]!) {
    photoDownloadUrls(photos: $photos) {
      photo_urls
    }
  }
`;

export const PhotoForStandaloneView = gql`
  query photoForStandaloneView($id: RecordID!) {
    photo(id: $id) {
      id
      thumbnail_urls {
        ...PhotoUrlTypeFields
      }
      ...PhotoForegroundDetails
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoForegroundDetails}
`;

export const PhotoForCommentsAndReactions = gql`
  query photoForCommentsAndReactions(
    $id: RecordID!
    $comments_limit: Int = 100
    $comments_offset: Int = 0
    $account_reactions_limit: Int = 50
    $account_reactions_offset: Int = 0
    $parent_id: RecordID
    $root_only: Boolean
  ) {
    photo(id: $id) {
      id
      is_owner
      is_uploader
      description
      comments(
        slice: { limit: $comments_limit, offset: $comments_offset }
        parent_id: $parent_id
        root_only: $root_only
      ) {
        total
        records {
          id
          ...CommentDetails
        }
      }
      grouped_reactions(slice: { limit: 999 }) {
        total
        records {
          id
          ...GroupedReactionsDetails
        }
      }
      account_file_reaction {
        ...AccountFileReactionDetails
      }
      reactions_accounts(
        slice: {
          limit: $account_reactions_limit
          offset: $account_reactions_offset
        }
      ) {
        total
        records {
          ...ReactionAccountDetails
        }
      }
    }
  }
  ${CommentDetails}
  ${GroupedReactionsDetails}
  ${AccountFileReactionDetails}
  ${ReactionAccountDetails}
`;

export const PhotoForCaption = gql`
  query PhotoForCaption($id: RecordID!) {
    photo(id: $id) {
      id
      is_owner
      is_uploader
      description
    }
  }
`;

export const PhotoForAlbumPhotoStatus = gql`
  query PhotoForAlbumPhotoStatus($id: RecordID!, $album_id: RecordID!) {
    photo(id: $id) {
      id
      album_photo_status(album_id: $album_id)
      album_oauth_link_photo_exists(album_id: $album_id)
    }
  }
`;

export const PhotoForFaceHighlights = gql`
  query PhotoForFaceHighlights($id: RecordID!, $album_id: RecordID!) {
    photo(id: $id) {
      id
      ...PhotoFaceHighlightDetails
    }
  }
  ${PhotoFaceHighlightDetails}
`;

export const AlbumNjrBibSettings = gql`
  query albumNjrBibSettings($albumId: RecordID!) {
    album(id: $albumId) {
      id
      root_album_folder {
        id
        uniform_tags: tags(
          slice: { limit: 1 }
          album_folder_tag_types: [UNIFORM]
        ) {
          records {
            id
            ... on UniformAlbumFolderTag {
              uniform_enabled
              uniform_numbered
              text_matching_enabled
            }
          }
        }
      }
    }
  }
`;

export const PhotoNjrBibMatches = gql`
  query photoNjrBibMatches($id: RecordID!, $albumIds: [RecordID] = []) {
    photo(id: $id) {
      id
      faces(
        slice: { limit: 999 }
        exclude_machine_labeled_pub_boxes: false
        exclude_human_labeled_pub_boxes: false
      ) {
        records {
          id
          facebox
          faux_face_type
          matched_photos(slice: { limit: 999 }, album_ids: $albumIds) {
            records {
              id
              identity {
                id
                name
                last_name
                current_matching_face {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
